import { motion, useMotionValue, useScroll, useSpring, useTransform } from 'framer-motion';
import { useCallback, useRef, useState, type PointerEvent } from 'react';

/* -------------------------------------------------------------------------- */
/*  Constants                                                                 */
/* -------------------------------------------------------------------------- */

/** Unique prefix to prevent SVG filter/gradient/mask ID collisions. */
const UID = 'ww';

/** Decorative thread paths — the five coloured silk threads that enter from the left. */
const threads = [
  { d: 'M-30 382C110 330 164 95 354 166C512 225 524 310 696 312', color: '#d9a227' },
  { d: 'M-30 402C135 364 179 145 368 198C493 233 561 292 776 310', color: '#a51643' },
  { d: 'M-30 422C164 404 214 201 395 231C524 253 603 294 854 312', color: '#7e3d91' },
  { d: 'M-30 442C190 448 253 260 428 266C577 270 664 313 946 314', color: '#196b58' },
  { d: 'M-30 462C233 493 291 318 466 301C625 286 734 324 1042 315', color: '#e05b32' },
];

/** Colour palette used by the woven weft (horizontal) threads. */
const weaveColors = ['#efc96f', '#d9a227', '#a51643', '#7e3d91', '#196b58', '#e05b32'];

/** Pre-computed weft (horizontal) thread paths inside the wordmark mask. */
const weftThreads = Array.from({ length: 16 }, (_, i) => {
  const y = 151 + i * 13;
  return {
    color: weaveColors[i % weaveColors.length],
    d: `M350 ${y}C545 ${y - 15} 690 ${y + 14} 870 ${y}C1048 ${y - 14} 1210 ${y + 15} 1455 ${y}`,
  };
});

/** Pre-computed warp (vertical) thread paths inside the wordmark mask. */
const warpThreads = Array.from({ length: 12 }, (_, i) => {
  const x = 470 + i * 72;
  return `M${x} 112C${x + 18} 185 ${x - 17} 276 ${x + 4} 382`;
});

/* -------------------------------------------------------------------------- */
/*  Shared text attributes                                                    */
/* -------------------------------------------------------------------------- */

const textAttrs = {
  x: 900,
  y: 328,
  textAnchor: 'middle' as const,
  fontFamily: 'Fraunces Variable, Fraunces, Georgia, serif',
  fontSize: 188,
  fontWeight: 500,
  letterSpacing: -7,
};

/* -------------------------------------------------------------------------- */
/*  Component                                                                 */
/* -------------------------------------------------------------------------- */

export interface WovenWordmarkProps {
  /** When false, all entrance animations are skipped (reduced-motion). */
  shouldAnimate: boolean;
}

export default function WovenWordmark({ shouldAnimate }: WovenWordmarkProps) {
  /* ---- interaction state ---- */
  const [isEngaged, setIsEngaged] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  /* ---- pointer-reactive motion values ---- */
  const rawPointerX = useMotionValue(0);
  const rawPointerY = useMotionValue(0);

  // Three independent spring tiers create cinematic depth:
  //  Background threads: slow, wide arc
  const bgSpringX = useSpring(rawPointerX, { stiffness: 80, damping: 30, mass: 1.4 });
  //  Wordmark: medium response
  const wmSpringX = useSpring(rawPointerX, { stiffness: 150, damping: 24, mass: 0.7 });
  const wmSpringY = useSpring(rawPointerY, { stiffness: 150, damping: 24, mass: 0.7 });
  //  Foreground tails: snappy, close-following
  const fgSpringX = useSpring(rawPointerX, { stiffness: 220, damping: 20, mass: 0.45 });

  // Parallax transforms — opposite direction so content feels "pinned"
  const bgX = useTransform(bgSpringX, (v) => v * -0.15);
  const wmX = useTransform(wmSpringX, (v) => v * -0.4);
  const wmY = useTransform(wmSpringY, (v) => v * -0.5);
  const fgX = useTransform(fgSpringX, (v) => v * -0.65);

  // Spotlight position — follows pointer in real-time (no spring damping)
  const spotlightX = useTransform(rawPointerX, (v) => 50 + v * 3);
  const spotlightY = useTransform(rawPointerY, (v) => 50 + v * 4);

  /* ---- scroll-linked vertical drift ---- */
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start end', 'end start'],
  });
  const scrollDrift = useTransform(scrollYProgress, [0, 1], [28, -28]);

  /* ---- handlers ---- */
  const handlePointerMove = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      if (!shouldAnimate || e.pointerType === 'touch') return;

      const rect = e.currentTarget.getBoundingClientRect();
      const nx = (e.clientX - rect.left) / rect.width - 0.5;
      const ny = (e.clientY - rect.top) / rect.height - 0.5;

      rawPointerX.set(nx * 14);
      rawPointerY.set(ny * 10);
      setIsEngaged(true);
    },
    [rawPointerX, rawPointerY, shouldAnimate]
  );

  const handlePointerLeave = useCallback(() => {
    rawPointerX.set(0);
    rawPointerY.set(0);
    setIsEngaged(false);
  }, [rawPointerX, rawPointerY]);

  /* ---- animation helpers ---- */
  const reveal = shouldAnimate ? { width: [0, 1040] } : { width: 1040 };
  const threadEnter = () =>
    shouldAnimate ? { pathLength: [0, 1], opacity: [0, 0.92] } : { pathLength: 1, opacity: 0.82 };

  /* ---- render ---- */
  return (
    <div
      ref={containerRef}
      className="relative mx-auto max-w-[100rem] touch-pan-y overflow-hidden"
      aria-hidden="true"
      onPointerMove={handlePointerMove}
      onPointerLeave={handlePointerLeave}
    >
      {/* Ambient spotlight — radial gradient follows cursor */}
      {shouldAnimate && (
        <motion.div
          className="pointer-events-none absolute inset-0 z-10 opacity-0 mix-blend-soft-light transition-opacity duration-500"
          style={{
            // eslint-disable-next-line react-hooks/rules-of-hooks
            background: useTransform(
              [spotlightX, spotlightY],
              ([sx, sy]) => `radial-gradient(ellipse 38% 52% at ${sx}% ${sy}%, rgba(239,201,111,0.22), transparent 70%)`
            ),
            opacity: isEngaged ? 1 : 0,
          }}
        />
      )}

      <svg
        className="h-72 w-full sm:h-auto sm:min-h-72"
        viewBox="0 0 1440 470"
        fill="none"
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          {/* Shuttle glint — horizontal highlight band */}
          <linearGradient id={`${UID}-shuttle-glint`} x1="0" y1="0" x2="1" y2="0">
            <stop stopColor="#efc96f" stopOpacity="0" />
            <stop offset="0.5" stopColor="#fff4ca" stopOpacity="0.95" />
            <stop offset="1" stopColor="#efc96f" stopOpacity="0" />
          </linearGradient>

          {/* Zari glow — Gaussian bloom on the text stroke */}
          <filter id={`${UID}-zari-glow`} x="-15%" y="-30%" width="130%" height="160%">
            <feGaussianBlur stdDeviation="6" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          {/* Ambient glow pulse — breathing luminance on the wordmark at rest */}
          <filter id={`${UID}-ambient-pulse`} x="-20%" y="-35%" width="140%" height="170%">
            <feGaussianBlur stdDeviation="8" result="softBlur" />
            <feMerge>
              <feMergeNode in="softBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          {/* Mask — wordmark silhouette clips the warp & weft weave */}
          <mask id={`${UID}-wordmark-mask`} maskUnits="userSpaceOnUse" x="350" y="100" width="1080" height="290">
            <text {...textAttrs} fill="white">
              Sree Silks
            </text>
          </mask>

          {/* Reveal clip — wipes left-to-right on viewport entry */}
          <clipPath id={`${UID}-wordmark-reveal`}>
            <motion.rect
              x="365"
              y="120"
              height="245"
              initial={false}
              whileInView={reveal}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 1.9, delay: 0.72, ease: [0.22, 1, 0.36, 1] }}
            />
          </clipPath>

          {/* Shimmer filter — subtle brightness oscillation on weave threads */}
          <filter id={`${UID}-shimmer`} x="-5%" y="-5%" width="110%" height="110%">
            <feTurbulence type="fractalNoise" baseFrequency="0.015 0.06" numOctaves="2" seed="7" result="noise">
              {shouldAnimate && (
                <animate
                  attributeName="baseFrequency"
                  values="0.015 0.06;0.022 0.08;0.015 0.06"
                  dur="8s"
                  repeatCount="indefinite"
                />
              )}
            </feTurbulence>
            <feDisplacementMap
              in="SourceGraphic"
              in2="noise"
              scale={isEngaged ? 2.8 : 1.2}
              xChannelSelector="R"
              yChannelSelector="G"
            />
          </filter>
        </defs>

        {/* Master group — scales down on mobile */}
        <g className="origin-[900px_280px] scale-[1.3] sm:scale-100">
          {/* ---- Layer 1: Background decorative threads (slowest parallax) ---- */}
          <motion.g style={{ x: bgX, y: useTransform(scrollDrift, (v) => v * 0.4) }}>
            {threads.map((thread, i) => (
              <motion.path
                key={`bg-${thread.color}`}
                d={thread.d}
                stroke={thread.color}
                strokeWidth={i === 0 ? 2.8 : 1.85}
                strokeLinecap="round"
                initial={false}
                whileInView={threadEnter()}
                viewport={{ once: true, amount: 0.3 }}
                transition={{
                  duration: 1.9,
                  delay: i * 0.12,
                  ease: [0.22, 1, 0.36, 1],
                }}
              />
            ))}
          </motion.g>

          {/* ---- Layer 2: Wordmark + woven interior (medium parallax) ---- */}
          <motion.g clipPath={`url(#${UID}-wordmark-reveal)`} style={{ x: wmX, y: wmY }}>
            {/* Ghost fill — very faint gold so the text isn't invisible before glow */}
            <text
              {...textAttrs}
              fill="#d9a227"
              fillOpacity={0.18}
              stroke="#efc96f"
              strokeOpacity={0.55}
              strokeWidth={1.2}
            >
              Sree Silks
            </text>

            {/* Warp & weft weave interior, clipped to wordmark shape */}
            <g mask={`url(#${UID}-wordmark-mask)`}>
              <g filter={isEngaged ? `url(#${UID}-shimmer)` : undefined}>
                {/* Warp (vertical) threads */}
                {warpThreads.map((d, i) => (
                  <motion.path
                    key={`warp-${d}`}
                    d={d}
                    stroke={i % 3 === 0 ? '#efc96f' : '#a66f0b'}
                    strokeWidth={i % 3 === 0 ? 1.6 : 0.9}
                    opacity={i % 3 === 0 ? 0.78 : 0.45}
                    initial={false}
                    whileInView={shouldAnimate ? { pathLength: [0, 1] } : { pathLength: 1 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{
                      duration: 1.3,
                      delay: 0.8 + i * 0.035,
                      ease: [0.22, 1, 0.36, 1],
                    }}
                  />
                ))}

                {/* Weft (horizontal) threads */}
                {weftThreads.map((thread, i) => (
                  <motion.path
                    key={`weft-${i}`}
                    d={thread.d}
                    stroke={thread.color}
                    strokeWidth={i % 4 === 0 ? 3.8 : 2.8}
                    strokeLinecap="round"
                    initial={false}
                    whileInView={
                      shouldAnimate ? { pathLength: [0, 1], opacity: [0, 0.95] } : { pathLength: 1, opacity: 0.88 }
                    }
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{
                      duration: 1.45,
                      delay: 0.68 + i * 0.035,
                      ease: [0.22, 1, 0.36, 1],
                    }}
                  />
                ))}
              </g>

              {/* Shuttle glint sweep — slides across the weave on viewport entry */}
              <motion.rect
                x="300"
                y="112"
                width="210"
                height="270"
                fill={`url(#${UID}-shuttle-glint)`}
                initial={false}
                whileInView={shouldAnimate ? { x: [0, 920], opacity: [0, 0.9, 0] } : { x: 920, opacity: 0 }}
                viewport={{ once: true, amount: 0.35 }}
                transition={{
                  duration: 1.25,
                  delay: 1.72,
                  ease: [0.16, 1, 0.3, 1],
                }}
              />
            </g>

            {/* Ambient breathing glow — soft halo that pulses at rest, intensifies on hover */}
            <motion.text
              {...textAttrs}
              fill="transparent"
              stroke="#d9a227"
              strokeWidth={2.5}
              filter={`url(#${UID}-ambient-pulse)`}
              initial={false}
              animate={
                isEngaged && shouldAnimate
                  ? { opacity: [0.35, 0.45], transition: { duration: 0.4, ease: 'easeOut' } }
                  : shouldAnimate
                    ? { opacity: [0.12, 0.22, 0.12], transition: { duration: 4, repeat: Infinity, ease: 'easeInOut' } }
                    : { opacity: 0.15 }
              }
            >
              Sree Silks
            </motion.text>

            {/* Outline text with glow — reacts to engagement */}
            <motion.text
              {...textAttrs}
              fill="transparent"
              stroke="#efc96f"
              strokeWidth={1.1}
              filter={`url(#${UID}-zari-glow)`}
              initial={false}
              animate={isEngaged && shouldAnimate ? { opacity: 0.95 } : { opacity: 0.72 }}
              transition={{ duration: 0.28, ease: [0.25, 1, 0.5, 1] }}
            >
              Sree Silks
            </motion.text>
          </motion.g>

          {/* ---- Layer 3: Foreground thread tails (fastest parallax) ---- */}
          <motion.g style={{ x: fgX, y: useTransform(scrollDrift, (v) => v * -0.6) }}>
            {threads.map((thread, i) => (
              <motion.path
                key={`tail-${thread.color}`}
                d={`M${690 + i * 84} ${312 + (i % 2) * 2}C${930 + i * 36} ${330 - i * 8} ${1190 + i * 30} ${190 + i * 37} 1490 ${245 + i * 31}`}
                stroke={thread.color}
                strokeWidth={1.5}
                strokeLinecap="round"
                initial={false}
                whileInView={
                  shouldAnimate ? { pathLength: [0, 1], opacity: [0, 0.6] } : { pathLength: 1, opacity: 0.5 }
                }
                viewport={{ once: true, amount: 0.25 }}
                transition={{
                  duration: 1.8,
                  delay: 1.15 + i * 0.1,
                  ease: [0.22, 1, 0.36, 1],
                }}
              />
            ))}
          </motion.g>
        </g>
      </svg>

      {/* Bottom fade into footer body */}
      <div className="absolute inset-x-0 bottom-0 h-24 bg-linear-to-t from-[#090a17] to-transparent" />
    </div>
  );
}
